// `/forgot`, WITH THE SERVER'S WEB_ACCOUNTS_ENABLED ON (ACC-3a; ACCOUNTS_PLAN §3 D1, D2).
//
// "Reset your password": the address, "Send link", and then D2 — THE SAME ANSWER WHETHER OR NOT AN ACCOUNT USES THE
// ADDRESS. The server sends E3 only to an account that does and answers `accepted` either way
// (`functions/src/v2/account/ops.ts`), so this page cannot tell and does not try: D2 says "If an account uses {email}".
// A refusal is shown by its approved sentence: C5 for a malformed address, C3 for too many tries, C9 otherwise.

import { useId, useState } from "react";
import { useLocation } from "react-router-dom";
import type { AuthRefusal } from "../../services";
import { useSession } from "../../app/session";
import { heldText } from "../../components/heldCopy";
import { accountState } from "./finish";
import { AccountForm, AuthError, EmailField, HeroTile, Say, SayTemplate, SubmitButton } from "./parts";
import { looksLikeEmail } from "./refusalCopy";

export function Forgot() {
  const session = useSession();
  const location = useLocation();
  const arrived = accountState(location.state);
  const [email, setEmail] = useState(arrived.email ?? "");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<AuthRefusal | null>(null);
  const [working, setWorking] = useState(false);
  const headingId = useId();

  const send = async () => {
    if (working) return;
    setRefusal(null);
    const typed = email.trim();
    if (!looksLikeEmail(typed)) return setRefusal({ kind: "notAnEmail" });
    setWorking(true);
    try {
      const result = await session.auth.sendPasswordReset(typed);
      if (!result.ok) return setRefusal(result.refusal);
      setSentTo(typed);
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="account-page" aria-labelledby={headingId} data-account-screen="forgot">
      <HeroTile />
      <h1 id={headingId} data-held-copy="account.forgot.heading">{heldText("account.forgot.heading")}</h1>
      <Say id="account.forgot.body" className="account-lead" />

      <AccountForm onSubmit={() => void send()}>
        <EmailField value={email} onChange={setEmail} invalid={refusal?.kind === "notAnEmail"} />
        <AuthError refusal={refusal} />
        <div role="status" className="live-slot">
          {sentTo !== null && (
            <div className="note good">
              <SayTemplate id="account.forgot.sent" values={{ email: sentTo }} className="copy" />
            </div>
          )}
        </div>
        <SubmitButton id="account.forgot.button" busy={working} />
      </AccountForm>
    </section>
  );
}
